import { del } from '@/utils';
import { store } from '@/utils/redux/config';
import { IUploadedFile } from '@/hooks/fetch-hooks/use-file-uploader/types';
import { assetsActions } from './slice';
import { IAssetItem } from './types';

const getCurrentAssetRoute = (): string => {
	if (typeof window === 'undefined') {
		return '';
	}

	return window.location.pathname;
};

const trackUploadedAssets = (
	assets: IUploadedFile[] | IUploadedFile,
	route: string = getCurrentAssetRoute()
): void => {
	const assetsArray = Array.isArray(assets) ? assets : [assets];
	const payload: IAssetItem[] = assetsArray
		.filter((asset) => asset?.id && asset?.publicUrl)
		.map((asset) => ({
			id: asset.id,
			publicUrl: asset.publicUrl,
			route
		}));

	if (payload.length > 0) {
		store.dispatch(assetsActions.addAssets(payload));
	}
};

const consumeTrackedAssetsByIds = (assetIds: number[]): void => {
	if (assetIds.length === 0) {
		return;
	}

	store.dispatch(assetsActions.removeAssetsByIds(assetIds));
};

const getTrackedAssets = (): IAssetItem[] => store.getState().assets?.items || [];

const getTrackedAssetsByRoute = (route: string): IAssetItem[] =>
	getTrackedAssets().filter((item) => item.route === route);

const getTrackedAssetsByUrls = (publicUrls: string[]): IAssetItem[] =>
	getTrackedAssets().filter((item) => publicUrls.includes(item.publicUrl));

const consumeTrackedAssetsByRoute = (route: string = getCurrentAssetRoute()): void => {
	if (!route) {
		return;
	}

	store.dispatch(assetsActions.removeAssetsByRoute(route));
};

const consumeTrackedAssetsByUrls = (publicUrls: string[]): void => {
	const trackedAssets = getTrackedAssetsByUrls(publicUrls);
	consumeTrackedAssetsByIds(trackedAssets.map((item) => item.id));
};

const deleteAssetFromBucket = async (assetId: number): Promise<void> => {
	await del(`api/assets/${assetId}`);
};

const cleanupTrackedAssetsByIds = async (assetIds: number[]): Promise<void> => {
	if (assetIds.length === 0) {
		return;
	}

	await Promise.allSettled(assetIds.map((assetId) => deleteAssetFromBucket(assetId)));
	store.dispatch(assetsActions.removeAssetsByIds(assetIds));
};

const cleanupTrackedAssetsByRoute = async (
	route: string = getCurrentAssetRoute()
): Promise<void> => {
	if (!route) {
		return;
	}

	const trackedAssets = getTrackedAssetsByRoute(route);
	await cleanupTrackedAssetsByIds(trackedAssets.map((item) => item.id));
};

const cleanupTrackedAssetsByUrls = async (publicUrls: string[]): Promise<void> => {
	const trackedAssets = getTrackedAssetsByUrls(publicUrls);
	await cleanupTrackedAssetsByIds(trackedAssets.map((item) => item.id));
};

export {
	getCurrentAssetRoute,
	trackUploadedAssets,
	deleteAssetFromBucket,
	consumeTrackedAssetsByIds,
	consumeTrackedAssetsByRoute,
	consumeTrackedAssetsByUrls,
	cleanupTrackedAssetsByRoute,
	cleanupTrackedAssetsByUrls
};
